import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/PageHeader";
import { api, apiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/format";
import { ArrowsClockwise, Sparkle } from "@phosphor-icons/react";

export default function Banking() {
  const [accounts, setAccounts] = useState([]);
  const [active, setActive] = useState("");
  const [txns, setTxns] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [parties, setParties] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", bank_name: "", account_no: "", ifsc: "", opening_balance: 0 });
  const [assign, setAssign] = useState({});

  const loadAccounts = useCallback(async () => {
    const { data } = await api.get("/banking/accounts");
    setAccounts(data);
    if (data.length) setActive((prev) => prev || data[0].id);
  }, []);

  const loadTxns = useCallback(async (accId) => {
    const { data } = await api.get(`/banking/transactions${accId ? `?account_id=${accId}` : ""}`);
    setTxns(data);
  }, []);

  useEffect(() => {
    loadAccounts().catch((e) => toast.error(apiError(e)));
    api.get("/parties").then((r) => setParties(r.data)).catch((e) => console.warn("parties", e));
    api.get("/ledgers").then((r) => setLedgers(r.data)).catch((e) => console.warn("ledgers", e));
  }, [loadAccounts]);

  useEffect(() => {
    if (active) loadTxns(active).catch((e) => toast.error(apiError(e)));
  }, [active, loadTxns]);

  const createAccount = async () => {
    try {
      await api.post("/banking/accounts", { ...form, opening_balance: Number(form.opening_balance) });
      toast.success("Bank account added with linked ledger");
      setOpen(false); setForm({ name: "", bank_name: "", account_no: "", ifsc: "", opening_balance: 0 });
      loadAccounts();
    } catch (e) { toast.error(apiError(e)); }
  };

  const demoFeed = async () => {
    try {
      const { data } = await api.post(`/banking/accounts/${active}/demo-feed`);
      toast.success(`${data.imported} statement lines imported`);
      loadTxns(active);
    } catch (e) { toast.error(apiError(e)); }
  };

  const runMatcher = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/banking/reconcile/auto?account_id=${active}`);
      setSuggestions(data.suggestions);
      toast.success(`${data.count} unmatched lines analysed`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const confirm = async (txnId, cand) => {
    try {
      await api.post("/banking/reconcile/confirm", { txn_id: txnId, voucher_id: cand.voucher_id, confidence: cand.confidence });
      toast.success("Matched");
      setSuggestions(suggestions.filter((s) => s.txn.id !== txnId));
      loadTxns(active);
    } catch (e) { toast.error(apiError(e)); }
  };

  const createVoucher = async (txnId) => {
    const sel = assign[txnId];
    if (!sel) { toast.error("Choose a party or ledger first"); return; }
    try {
      const [kind, id] = sel.split(":");
      await api.post("/banking/reconcile/create-voucher", kind === "party" ? { txn_id: txnId, party_id: id } : { txn_id: txnId, ledger_id: id });
      toast.success("Voucher created and matched");
      setSuggestions(suggestions.filter((s) => s.txn.id !== txnId));
      loadTxns(active);
    } catch (e) { toast.error(apiError(e)); }
  };

  const unmatched = txns.filter((t) => t.status === "unmatched");
  const matched = txns.filter((t) => t.status === "matched");

  return (
    <div className="space-y-6" data-testid="banking-page">
      <PageHeader title="Banking Hub" subtitle="Import statements, then let the AI matcher fuse amount, date, direction and narration signals.">
        {accounts.length > 0 && (
          <Select value={active} onValueChange={setActive}>
            <SelectTrigger className="h-8 w-56 text-sm" data-testid="bank-account-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.bank_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {active && <Button size="sm" variant="outline" onClick={demoFeed} data-testid="import-feed-btn">Import feed</Button>}
        {active && (
          <Button size="sm" onClick={runMatcher} disabled={busy} className="gap-1.5" data-testid="run-matcher-btn">
            <Sparkle size={14} /> {busy ? "Matching…" : "AI reconcile"}
          </Button>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="new-bank-account-btn">Add account</Button></DialogTrigger>
          <DialogContent data-testid="bank-account-dialog">
            <DialogHeader><DialogTitle className="font-display">Add bank account</DialogTitle></DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              {[["Ledger name", "name", "HDFC Bank - 4521"], ["Bank", "bank_name", "HDFC Bank"],
                ["Account number", "account_no", "50100234564521"], ["IFSC", "ifsc", "HDFC0000123"]].map(([label, key, ph]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest">{label}</Label>
                  <Input data-testid={`bank-${key}-input`} value={form[key]} placeholder={ph}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Opening balance</Label>
                <Input type="number" className="num" data-testid="bank-opening-input" value={form.opening_balance}
                  onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={createAccount} disabled={!form.name || !form.bank_name} data-testid="save-bank-account-btn">Add account</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {accounts.length === 0 ? (
        <Empty label="No bank accounts" hint="Add an account to import statements and reconcile." />
      ) : (
        <>
          <div className="grid gap-px border border-border bg-border sm:grid-cols-3">
            {[["Statement lines", txns.length], ["Unmatched", unmatched.length], ["Matched", matched.length]].map(([k, v]) => (
              <div key={k} className="bg-background p-4" data-testid={`bank-kpi-${k.replace(/\s/g, "-")}`}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
                <p className="num mt-1.5 text-xl font-semibold">{v}</p>
              </div>
            ))}
          </div>

          {suggestions.length > 0 && (
            <div className="panel" data-testid="suggestions-panel">
              <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <h2 className="font-display text-sm font-bold">AI match proposals</h2>
                <button onClick={runMatcher} className="text-muted-foreground hover:text-foreground" data-testid="rerun-matcher">
                  <ArrowsClockwise size={14} />
                </button>
              </div>
              <div className="divide-y divide-border">
                {suggestions.map((s) => (
                  <div key={s.txn.id} className="grid gap-4 p-4 lg:grid-cols-2" data-testid={`suggestion-${s.txn.id}`}>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Bank line · {fmtDate(s.txn.date)}</p>
                      <p className="mt-1 text-sm">{s.txn.description}</p>
                      <p className="num mt-1 text-lg font-semibold">
                        {s.txn.dr_cr === "Cr" ? "+" : "−"}{money(s.txn.amount)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      {s.candidates.length ? s.candidates.map((c) => (
                        <div key={c.voucher_id} className="flex items-start justify-between gap-3 border border-border p-2.5">
                          <div className="min-w-0">
                            <p className="num text-sm">{c.number} · {money(c.amount)}</p>
                            <p className="text-[11px] text-muted-foreground">{c.party_name || c.type} · {fmtDate(c.date)}</p>
                            <p className="mt-0.5 text-[10px] text-muted-foreground">{c.reasons.join(" · ")}</p>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="num text-xs font-semibold text-primary">{Math.round(c.confidence * 100)}%</p>
                            <Button size="sm" className="mt-1 h-6 text-[11px]" onClick={() => confirm(s.txn.id, c)}
                              data-testid={`accept-match-${s.txn.id}`}>Match</Button>
                          </div>
                        </div>
                      )) : (
                        <div className="flex flex-wrap items-center gap-2 border border-dashed border-border p-2.5">
                          <p className="w-full text-xs text-muted-foreground">No book entry found — create one:</p>
                          <Select value={assign[s.txn.id] || ""} onValueChange={(v) => setAssign({ ...assign, [s.txn.id]: v })}>
                            <SelectTrigger className="h-8 flex-1 text-xs" data-testid={`assign-select-${s.txn.id}`}>
                              <SelectValue placeholder="Party or ledger" />
                            </SelectTrigger>
                            <SelectContent className="max-h-64">
                              {parties.map((p) => <SelectItem key={p.id} value={`party:${p.id}`}>Party · {p.name}</SelectItem>)}
                              {ledgers.filter((l) => ["Indirect Expenses", "Direct Expenses", "Indirect Income"].includes(l.group))
                                .map((l) => <SelectItem key={l.id} value={`ledger:${l.id}`}>Ledger · {l.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button size="sm" className="h-8 text-xs" onClick={() => createVoucher(s.txn.id)}
                            data-testid={`create-voucher-${s.txn.id}`}>Create & match</Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-x-auto border border-border">
            <table className="dense-table w-full min-w-[760px]" data-testid="bank-txns-table">
              <thead><tr><th>Date</th><th>Description</th><th>Ref</th><th className="text-right">Amount</th><th>Dr/Cr</th><th>Status</th><th className="text-right">Confidence</th></tr></thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="row-in" data-testid={`txn-row-${t.id}`}>
                    <td className="num whitespace-nowrap">{fmtDate(t.date)}</td>
                    <td className="max-w-[320px] truncate">{t.description}</td>
                    <td className="num text-muted-foreground">{t.ref || "—"}</td>
                    <td className="num text-right font-medium">{money(t.amount)}</td>
                    <td className={t.dr_cr === "Cr" ? "text-success" : "text-destructive"}>{t.dr_cr}</td>
                    <td>
                      <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${
                        t.status === "matched" ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="num text-right text-muted-foreground">
                      {t.ml_confidence ? `${Math.round(t.ml_confidence * 100)}%` : "—"}
                    </td>
                  </tr>
                ))}
                {txns.length === 0 && <tr><td colSpan={7} className="text-muted-foreground">No statement lines. Use “Import feed”.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
